import { memo, useCallback, useRef } from 'react';

import { useDragNDropSort } from '@app/hooks/useDragNDropSort';
import DragHandle from '@mui/icons-material/DragHandle';
import { IconButton, TableCell, TableRow, Tooltip } from '@mui/material';

import { ActionInterface, ColumnInterface, GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { RowCell } from '../../../RowCell';
import { ActionIcon } from './ActionIcon';

export interface DefaultTableRowProps {
  columns: ColumnInterface[];
  index: number;
  row: GenericObject;
  rows: GenericObject[] | undefined;
  resource?: string;
  rowKey: string;
  orderable?: boolean;
  actions: ActionInterface[];
  reorderRecord?(dragIndex: number, hoverIndex: number): void;
}

export const DefaultTableRow = memo(
  ({ reorderRecord, orderable, index, columns, rows, row, actions, rowKey, resource }: DefaultTableRowProps) => {
    const getIndex = useCallback(
      (index: number) => {
        if (!rows?.length) return index;
        return index === rows?.length - 1 ? 'last' : index;
      },
      [rows]
    );

    const ref = useRef(null);
    const { handlerId, drop, isDragging, drag } = useDragNDropSort({
      ref,
      index,
      id: row[rowKey],
      moveCard: reorderRecord,
      direction: 'row',
    });

    const opacity = isDragging ? 0 : 1;
    drag(drop(ref));

    return (
      <TableRow
        ref={orderable ? ref : null}
        sx={{ cursor: orderable ? 'move' : 'auto', opacity }}
        data-handler-id={handlerId}
        data-cy={`row-${getIndex(index)}`}
        key={row[rowKey] as number | string}>
        {orderable && (
          <TableCell align="center">
            <DragHandle />
          </TableCell>
        )}
        {columns.map((column: ColumnInterface) => (
          <RowCell key={column.key} row={row} column={column} resource={resource} />
        ))}
        <TableCell align="center" sx={{ minWidth: 160 }}>
          {actions &&
            actions.length > 0 &&
            actions.map((action: ActionInterface) => (
              <Tooltip key={action.id} title={action.label}>
                <IconButton data-cy={`row-${getIndex(index)}-${action.id}`} onClick={() => action.action(row)}>
                  <ActionIcon action={action} row={row} />
                </IconButton>
              </Tooltip>
            ))}
        </TableCell>
      </TableRow>
    );
  }
);
